import { STATUS_OPTIONS } from "../config/constants";

// Mock address standardization API
export const mockStandardizeAddress = (address) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Ensure address is a string - handle objects, null, undefined
      let addressString = "";

      if (!address) {
        resolve("");
        return;
      }

      if (typeof address === "string") {
        addressString = address;
      } else if (typeof address === "object") {
        // If it's an object, skip standardization or convert to string
        console.warn("Address is an object, cannot standardize:", address);
        resolve("");
        return;
      } else {
        // Convert other types to string
        addressString = String(address);
      }

      // Only process if we have a valid string
      if (!addressString || addressString.trim() === "") {
        resolve("");
        return;
      }

      let standardized = addressString
        .toUpperCase()
        .replace(/ ST[,. ]/g, " STREET ")
        .replace(/ AVE[,. ]/g, " AVENUE ")
        .replace(/ LN[,. ]/g, " LANE ")
        .replace(/ RD[,. ]/g, " ROAD ")
        .replace(/\s+/g, " ")
        .trim();
      resolve(standardized);
    }, 700);
  });
};

/**
 * Extract address components from an item/record
 * Handles various field name formats (raw, mapped, A LOC fields)
 * @param {Object} item - Item/record object
 * @returns {Object|null} Object with { street_address, city, state, zip, country } or null if no address data
 */
export const extractAddressComponents = (item) => {
  if (!item) return null;

  // Try to find street_address in various formats
  const street_address =
    item.street_address ||
    item["Street Address"] ||
    item["A LOC Address"] ||
    item.address ||
    null;

  // Try to find city in various formats
  const city = item.city || item["City"] || item["A LOC City"] || null;

  // Try to find state in various formats
  const state = item.state || item["State"] || item["A LOC State"] || null;

  // Try to find zip in various formats
  const zip =
    item.zip ||
    item["ZIP"] ||
    item["Zip"] ||
    item["A LOC Zip"] ||
    item.zip_code ||
    null;

  // Try to find country in various formats
  const country = item.country || item["Country"] || "US"; // Default to US if not found

  // Return null if no address components found
  if (!street_address && !city && !state && !zip) {
    return null;
  }

  return {
    street_address: street_address || "",
    city: city || "",
    state: state || "",
    zip: zip || "",
    country: country || "US",
  };
};

/**
 * Format concatenated address from inventory record
 * Formats as: "Street, City, State, Country, ZIP"
 * ONLY uses standardized (std_*) fields - no fallback to raw fields
 * Also checks for standard_address field directly on record
 * @param {Object} record - Inventory record object
 * @returns {string} Formatted address string or "-" if no address data
 */
export const formatConcatenatedAddress = (record) => {
  if (!record) return "-";

  // If standard_address field exists directly on record, use it
  if (record.standard_address && record.standard_address.trim() !== "") {
    return record.standard_address;
  }

  const parts = [];

  // ONLY use standardized fields - no fallback to raw fields
  const street = record.std_street_address || null;
  const city = record.std_city || null;
  const state = record.std_state || null;
  const country = record.std_country || null;
  const zip = record.std_zip || null;

  // Build address from standardized components only
  if (street) parts.push(street);
  if (city) parts.push(city);
  if (state) parts.push(state);
  if (country) parts.push(country);
  if (zip) parts.push(zip);

  return parts.length > 0 ? parts.join(", ") : "-";
};

/**
 * Parse a single address string into components
 * Handles formats like: "Street, City, State, ZIP" or "Street, City, State ZIP"
 * @param {string} addressString - Single address string to parse
 * @returns {Object} Object with { street_address, city, state, zip, country } or null if parsing fails
 */
export const parseAddressString = (addressString) => {
  if (!addressString || typeof addressString !== "string") {
    return null;
  }

  const trimmed = addressString.trim();
  if (!trimmed) {
    return null;
  }

  // Split by comma
  const parts = trimmed
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part);

  if (parts.length === 0) {
    return null;
  }

  // Common US state abbreviations (2 letters)
  const stateAbbreviations = [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
  ];

  let street_address = "";
  let city = "";
  let state = "";
  let zip = "";
  let country = "US"; // Default to US

  // Try to identify components
  // Common patterns:
  // 1. "Street, City, State, ZIP"
  // 2. "Street, City, State ZIP"
  // 3. "Street, City, State, Country, ZIP"
  // 4. "Street, City, State ZIP, Country"

  if (parts.length >= 2) {
    // First part is usually street address
    street_address = parts[0];

    // Last part might be ZIP or Country
    const lastPart = parts[parts.length - 1];
    const secondLastPart = parts.length >= 2 ? parts[parts.length - 2] : "";

    // Check if last part is a ZIP code (5 digits or 5+4 format)
    const zipMatch = lastPart.match(/^(\d{5})(-\d{4})?$/);
    if (zipMatch) {
      zip = lastPart;
      // Second last might be state or country
      if (parts.length >= 3) {
        const stateCandidate = secondLastPart;
        // Check if it's a state abbreviation
        if (stateAbbreviations.includes(stateCandidate.toUpperCase())) {
          state = stateCandidate.toUpperCase();
          // Everything between street and state is city
          if (parts.length >= 4) {
            city = parts.slice(1, parts.length - 2).join(", ");
          } else if (parts.length === 3) {
            city = parts[1];
          }
        } else {
          // Might be country, check second last
          if (parts.length >= 4) {
            const stateCandidate2 = parts[parts.length - 3];
            if (stateAbbreviations.includes(stateCandidate2.toUpperCase())) {
              state = stateCandidate2.toUpperCase();
              country = stateCandidate;
              city = parts.slice(1, parts.length - 3).join(", ");
            } else {
              // No state found, assume second last is state (full name)
              state = stateCandidate;
              city = parts.slice(1, parts.length - 2).join(", ");
            }
          } else {
            // Assume second last is state
            state = stateCandidate;
            city = parts[1];
          }
        }
      } else if (parts.length === 2) {
        // Only street and ZIP - not enough info
        city = "";
        state = "";
      }
    } else {
      // Last part is not ZIP, might be country or state
      // Check if it's a state abbreviation
      if (stateAbbreviations.includes(lastPart.toUpperCase())) {
        state = lastPart.toUpperCase();
        // Everything between street and state is city
        if (parts.length >= 3) {
          city = parts.slice(1, parts.length - 1).join(", ");
        }
      } else if (parts.length >= 3) {
        // Check second last for state
        const stateCandidate = secondLastPart;
        if (stateAbbreviations.includes(stateCandidate.toUpperCase())) {
          state = stateCandidate.toUpperCase();
          country = lastPart;
          city = parts.slice(1, parts.length - 2).join(", ");
        } else {
          // Assume last is state (full name), second last might be city
          state = lastPart;
          city = parts.slice(1, parts.length - 1).join(", ");
        }
      } else if (parts.length === 2) {
        // Only street and city
        city = parts[1];
      }
    }
  } else if (parts.length === 1) {
    // Only one part - assume it's street address
    street_address = parts[0];
  }

  // If we couldn't parse properly, return null
  if (!street_address) {
    return null;
  }

  return {
    street_address: street_address || "",
    city: city || "",
    state: state || "",
    zip: zip || "",
    country: country || "US",
  };
};

// CSV parsing utility
export const parseCSV = (csv) => {
  const lines = csv.trim().split("\n");
  const headers = lines[0].split(",").map((h) => h.trim());
  return lines.slice(1).map((line, index) => {
    const values = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g) || [];
    const record = headers.reduce((obj, header, i) => {
      let value = (values[i] || "").trim().replace(/^"|"$/g, "");
      obj[header] = header === "mrc" && value ? parseFloat(value) : value;
      return obj;
    }, {});
    record.temp_id = index;
    return record;
  });
};

/** Round up to the nearest whole dollar (e.g. 20.35 → 21). */
export const roundUpToDollar = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return Math.ceil(num);
};

/** Report currency: round up, no cents. */
export const formatReportCurrency = (value) => {
  const rounded = roundUpToDollar(value);
  if (rounded === null) return null;
  const abs = Math.abs(rounded);
  const formatted = abs.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return rounded < 0 ? `-$${formatted}` : `$${formatted}`;
};

/** Signed report currency for variance (+ over, − under). */
export const formatReportVariance = (value) => {
  const rounded = roundUpToDollar(value);
  if (rounded === null) return null;
  const abs = Math.abs(rounded);
  const formatted = abs.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  if (rounded > 0) return `+$${formatted}`;
  if (rounded < 0) return `-$${formatted}`;
  return "$0";
};

// Format currency
export const formatCurrency = (amount) => {
  if (!amount || amount === null || amount === undefined || isNaN(amount)) return "$0.00";

  const numValue = parseFloat(amount);

  const formatted = Math.abs(numValue).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return numValue < 0 ? `-$${formatted}` : `$${formatted}`;
};

// Format number with thousand separators (no currency symbol)
export const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "";
  return numValue.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const safeDateOnly = (dateString) => {
  if (!dateString) return null;

  // If already ISO date-only
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // If full ISO datetime → strip safely
  if (dateString.includes("T")) {
    return dateString.split("T")[0];
  }

  return null;
};

// Format date
// export const formatDate = (dateString) => {
//   if (!dateString) return "-";

//   try {
//     const date = new Date(dateString);
//     if (isNaN(date.getTime())) return "-";

//     return date.toLocaleDateString("en-US", {
//       year: "numeric",
//       month: "2-digit",
//       day: "2-digit",
//     });
//   } catch (error) {
//     console.error("Error formatting date:", dateString, error);
//     return "-";
//   }
// };

export const formatDate = (dateString) => {
  const d = safeDateOnly(dateString);
  if (!d) return "-";

  return d; // YYYY-MM-DD (global consistent)
};

// Format date for display in tables
// export const formatTableDate = (dateString) => {
//   if (!dateString) return "-";

//   try {
//     const date = new Date(dateString);
//     if (isNaN(date.getTime())) return "-";

//     return date.toLocaleDateString("en-US", {
//       year: "numeric",
//       month: "short",
//       day: "2-digit",
//     });
//   } catch (error) {
//     console.error("Error formatting table date:", dateString, error);
//     return "-";
//   }
// };

export const formatTableDate = (dateString) => {
  const d = safeDateOnly(dateString);
  if (!d) return "-";
  return d; // YYYY-MM-DD — consistent with inventory tables and edit forms
};

// Get unique values from array
export const getUniqueValues = (array, key) => {
  return [...new Set(array.map((item) => item[key]))];
};

/**
 * Normalize status value to match standard STATUS_OPTIONS
 * Maps various status formats (case-insensitive) to standard values:
 * - "Active", "active", "ACTIVE", "In Service" -> "Active"
 * - "Pending Install", "pending install", "Pending" -> "Pending Install"
 * - "Disconnected", "disconnected" -> "Disconnected"
 * - "Pending Disconnect", "pending disconnect" -> "Pending Disconnect"
 * @param {string} status - Raw status value from data
 * @param {Array<string>} statusOptions - Array of standard status options (default: STATUS_OPTIONS)
 * @returns {string} Normalized status value matching one of the standard options, or original value if no match
 */
export const normalizeStatus = (status, statusOptions = null) => {
  if (!status || typeof status !== "string") {
    return status || "";
  }

  // Use provided statusOptions or fall back to STATUS_OPTIONS constant
  const standardOptions = statusOptions || STATUS_OPTIONS;

  const statusLower = status.toLowerCase().trim();

  // Map common variations to standard options
  // Check for exact matches first (case-insensitive)
  for (const standardStatus of standardOptions) {
    if (statusLower === standardStatus.toLowerCase()) {
      return standardStatus;
    }
  }

  // Handle common variations and aliases
  if (
    statusLower === "in service" ||
    statusLower === "active" ||
    statusLower.startsWith("active")
  ) {
    return (
      standardOptions.find((s) => s.toLowerCase() === "active") || "Active"
    );
  }

  if (
    statusLower === "pending install" ||
    statusLower === "pending" ||
    (statusLower.startsWith("pending") && !statusLower.includes("disconnect"))
  ) {
    return (
      standardOptions.find((s) => s.toLowerCase() === "pending install") ||
      "Pending Install"
    );
  }

  if (
    statusLower === "disconnected" ||
    (statusLower.includes("disconnect") && !statusLower.includes("pending"))
  ) {
    return (
      standardOptions.find((s) => s.toLowerCase() === "disconnected") ||
      "Disconnected"
    );
  }

  if (
    statusLower === "pending disconnect" ||
    (statusLower.includes("pending") && statusLower.includes("disconnect"))
  ) {
    return (
      standardOptions.find((s) => s.toLowerCase() === "pending disconnect") ||
      "Pending Disconnect"
    );
  }

  // Return original value if no match found
  return status;
};

// Field mapping should come from API - this is now deprecated
// Kept for backward compatibility only
export const getFieldMapping = () => {
  // Return empty object - field mapping should come from API response
  console.warn(
    "getFieldMapping() is deprecated. Use API response for field mapping."
  );
  return {};
};

// Get field value with fallbacks
export const getFieldValue = (item, fieldName, columnName) => {
  if (!fieldName || !item) return "-";

  let value = item[fieldName];

  // Handle fallback fields
  if (!value || value === "" || value === null || value === undefined) {
    switch (fieldName) {
      case "service_type":
        value = item.category || item.service_type || item.component_name;
        break;
      case "status":
        value = item.status || item.circuit_status_code;
        break;
      case "street_address":
        value = item.address || item.street_address || item.a_loc_address;
        break;
      case "city":
        value = item.city || item.a_loc_city;
        break;
      case "state":
        value = item.state || item.a_loc_state;
        break;
      case "zip":
        value = item.zip_code || item.zip || item.a_loc_zip;
        break;
      case "bandwidth":
        value =
          item.bandwidth ||
          item.speed ||
          item.band_width ||
          item.bandwidth_capacity;
        break;
      case "last_mile_provider":
        value = item.provider || item.last_mile_provider || item.lec_id;
        break;
      case "contract_term":
        value = item.contract_term || item.contract_term_months;
        break;
      case "end_of_term":
        value = item.end_of_term || item.expiration;
        break;
      case "mrc":
        value = item.mrc || item.usd_amount;
        break;
      case "billing_account":
        value = item.billing_account || item.billing_account_number;
        break;
      default:
        value = item[fieldName];
    }
  }

  return value || "-";
};

// Format field value based on column type
// Now fully dynamic - no hardcoded column names
export const formatFieldValue = (value, columnName, item) => {
  if (!value || value === "-") return "-";

  // Format based on value type, not hardcoded column names
  if (typeof value === "number" && value > 1000) {
    // Likely a currency value
    return formatCurrency(value);
  } else if (value.match && value.match(/^\d{4}-\d{2}-\d{2}/)) {
    // Likely a date
    return formatTableDate(value);
  }

  return value;
};

// Filter inventory items
export const filterInventory = (inventory, filters) => {
  return inventory.filter((item) => {
    const searchLower = filters.search.toLowerCase();
    return (
      (item.circuit_id.toLowerCase().includes(searchLower) ||
        (item.address && item.address.toLowerCase().includes(searchLower))) &&
      (filters.provider === "all" || item.provider === filters.provider) &&
      (filters.category === "all" || item.category === filters.category)
    );
  });
};

// Pagination utility
export const paginate = (array, page, itemsPerPage) => {
  const startIndex = (page - 1) * itemsPerPage;
  return array.slice(startIndex, startIndex + itemsPerPage);
};

// Normalize data to ensure proper types
export const normalizeInventoryData = (data) => {
  if (!Array.isArray(data)) return data;

  return data.map((item) => ({
    ...item,
    // Always preserve ID - convert to number if it exists, but keep original if conversion fails
    id:
      item.id !== undefined && item.id !== null
        ? isNaN(parseInt(item.id))
          ? item.id
          : parseInt(item.id)
        : item.id,
    // Explicitly preserve provider field - do not overwrite with last_mile_provider
    // Provider field is preserved from original item via spread operator above
    mrc: item.mrc ? parseFloat(item.mrc) : null,
    contract_term: item.contract_term ? parseInt(item.contract_term) : null,
    address_id: item.address_id ? parseInt(item.address_id) : null,
    job_id: item.job_id ? parseInt(item.job_id) : null,
    // Normalize bandwidth field with multiple possible field names
    bandwidth:
      item.bandwidth ||
      item.speed ||
      item.band_width ||
      item.bandwidth_capacity ||
      null,
    // Normalize Lumen-specific fields
    component_name: item.component_name || item.service_type || null,
    circuit_status_code: item.circuit_status_code || item.status || null,
    a_loc_address:
      item.a_loc_address || item.street_address || item.address || null,
    a_loc_city: item.a_loc_city || item.city || null,
    a_loc_state: item.a_loc_state || item.state || null,
    a_loc_zip: item.a_loc_zip || item.zip || item.zip_code || null,
    bandwidth_capacity: item.bandwidth_capacity || item.bandwidth || null,
    lec_id: item.lec_id || item.last_mile_provider || null,
    contract_term_months:
      item.contract_term_months || item.contract_term || null,
    expiration: item.expiration || item.end_of_term || null,
    usd_amount: item.usd_amount || item.mrc || null,
    billing_account_number:
      item.billing_account_number || item.billing_account || null,
  }));
};

/**
 * Format concatenated input address from address record
 * Formats as: "Street, City, State, Country, ZIP"
 * @param {Object} address - Address record object
 * @returns {string} Formatted address string or "-" if no address data
 */
export const formatInputAddress = (address) => {
  if (!address) return "-";

  const parts = [];

  const street = address.street_address || null;
  const city = address.city || null;
  const state = address.state || null;
  const country = address.country || null;
  const zip = address.zip || null;

  if (street) parts.push(street);
  if (city) parts.push(city);
  if (state) parts.push(state);
  if (country) parts.push(country);
  if (zip) parts.push(zip);

  return parts.length > 0 ? parts.join(", ") : "-";
};

/**
 * Format concatenated standard address from address record
 * Formats as: "Street, City, State, Country, ZIP"
 * @param {Object} address - Address record object
 * @returns {string} Formatted address string or "-" if no address data
 */
export const formatStandardAddress = (address) => {
  if (!address) return "-";

  const parts = [];

  const street = address.std_street_address || null;
  const city = address.std_city || null;
  const state = address.std_state || null;
  const country = address.std_country || null;
  const zip = address.std_zip || null;

  if (street) parts.push(street);
  if (city) parts.push(city);
  if (state) parts.push(state);
  if (country) parts.push(country);
  if (zip) parts.push(zip);

  return parts.length > 0 ? parts.join(", ") : "-";
};

/**
 * Format bandwidth value to display as "Full / Available" format
 * Handles formats like "1 Gbps / 35 Mbps", "1000 Mbps / 35 Mbps", or single values
 * @param {string|number} value - Bandwidth value (can be single value or "X / Y" format)
 * @param {string|number} availableValue - Optional available bandwidth value
 * @returns {string} Formatted bandwidth string like "1 Gbps / 35 Mbps" or "-" if no value
 */
export const formatBandwidth = (value, availableValue = null) => {
  if (!value && !availableValue) return "-";

  // Helper function to normalize and format a single bandwidth value
  const normalizeBandwidthValue = (val) => {
    if (!val) return null;
    const str = String(val).trim();
    if (!str || str === "-") return null;

    const strLower = str.toLowerCase();
    
    // Check if already contains unit (mbps, gbps, kbps, mb, gb, kb)
    // Use word boundary checks to avoid false positives
    const hasUnit = 
      /\b(mbps|gbps|kbps|mb|gb|kb)\b/i.test(str) ||
      strLower.includes("mbps") ||
      strLower.includes("gbps") ||
      strLower.includes("kbps") ||
      (strLower.includes("mb") && !strLower.includes("number")) ||
      (strLower.includes("gb") && !strLower.includes("number")) ||
      (strLower.includes("kb") && !strLower.includes("number"));
    
    if (hasUnit) {
      // Return as-is if it already has a unit
      return str;
    }

    // Check if the string looks like it might be part of a bandwidth expression
    // (e.g., contains "/" or other bandwidth-related patterns)
    // If so, preserve it as-is
    if (str.includes("/") || str.match(/[\d.]+\s*[\/\-\s]+/)) {
      return str;
    }

    // Try to parse as number
    const numValue = parseFloat(str);
    if (!isNaN(numValue)) {
      // Only add Mbps unit if it's a plain number without any context
      // This handles cases where the value might have been stripped of its unit
      // but we want to be conservative and only add units when we're sure
      return `${numValue} Mbps`;
    }

    // Return original string if can't parse
    return str;
  };

  // Check if value already contains "/" separator (already in "full / available" format)
  const valueStr = String(value || "").trim();
  if (valueStr.includes("/")) {
    const parts = valueStr.split("/").map((p) => p.trim());
    if (parts.length === 2) {
      const full = normalizeBandwidthValue(parts[0]);
      const available = normalizeBandwidthValue(parts[1]);
      if (full && available) {
        return `${full} / ${available}`;
      } else if (full) {
        return full;
      } else if (available) {
        return available;
      }
    }
  }

  // If we have both value and availableValue, combine them
  if (value && availableValue) {
    const full = normalizeBandwidthValue(value);
    const available = normalizeBandwidthValue(availableValue);
    if (full && available) {
      return `${full} / ${available}`;
    } else if (full) {
      return full;
    } else if (available) {
      return available;
    }
  }

  // Single value case
  if (value) {
    const normalized = normalizeBandwidthValue(value);
    return normalized || "-";
  }

  // Only available value
  if (availableValue) {
    const normalized = normalizeBandwidthValue(availableValue);
    return normalized || "-";
  }

  return "-";
};

/**
 * Format phone number to (XXX) XXX-XXXX format
 * Uses best-effort approach to format various digit lengths
 * @param {string|number} phoneNumber - Phone number to format (can contain non-digits)
 * @returns {string} Formatted phone number or original value if no digits found
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  
  // Convert to string and extract only digits
  const digitsOnly = String(phoneNumber).replace(/\D/g, "");
  
  // If no digits found, return original value
  if (!digitsOnly) return String(phoneNumber);
  
  // Format based on number of digits (best-effort approach)
  const len = digitsOnly.length;
  
  if (len === 10) {
    // Standard US format: (XXX) XXX-XXXX
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  } else if (len === 11 && digitsOnly[0] === "1") {
    // 11 digits starting with 1: 1 (XXX) XXX-XXXX
    return `${digitsOnly[0]} (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
  } else if (len === 7) {
    // 7 digits: XXX-XXXX
    return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
  } else if (len > 10) {
    // More than 10 digits (but not 11 starting with 1): format last 10 digits
    const last10 = digitsOnly.slice(-10);
    const prefix = digitsOnly.slice(0, -10);
    return `${prefix} (${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
  } else if (len > 7) {
    // 8-9 digits: Try to format as (XXX) XXX-X... or (XXX) XXXX...
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
  } else if (len > 3) {
    // 4-6 digits: XXX-X...
    return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
  }
  
  // Less than 4 digits or other cases: return digits as-is
  return digitsOnly;
};


/** True when value looks like a phone/service number (10–15 digits). */
export const isPhoneLikeCircuitId = (value) => {
  if (value == null || value === "") return false;
  const original = String(value).trim();
  const digitsOnly = original.replace(/\D/g, "");
  // Must be phone-length AND not an alphanumeric circuit code like SV123 / AG456
  if (digitsOnly.length < 10 || digitsOnly.length > 15) return false;
  if (/[A-Za-z]/.test(original)) return false;
  return true;
};

/** Format circuit_id for UI: phone-format only when phone-like; else show raw. */
export const formatCircuitIdForDisplay = (value) => {
  if (value == null || value === "") return "";
  if (!isPhoneLikeCircuitId(value)) return String(value);
  return formatPhoneNumber(value);
};


/**
 * Normalize billing account for storage/display.
 * Removes whitespace and dashes so "831-001-1234-1234" and "83100112341234" match.
 * @param {string} account - Billing account string
 * @returns {string} Normalized account string
 */
export const normalizeBillingAccount = (account) => {
  if (!account) return "";
  return String(account).trim().replace(/\s+/g, "").replace(/-/g, "");
};

/**
 * Build a mapping of billing account to provider from inventory data
 * Handles both grouped (with nested items) and non-grouped inventory data structures
 * Stores both normalized (no spaces) and original format keys to handle spacing variations
 * @param {Array} inventoryData - Inventory data (can be grouped or flat array)
 * @returns {Map<string, string>} Map of billing account -> provider
 */
export const buildBillingAccountProviderMap = (inventoryData) => {
  const map = new Map();

  if (!inventoryData || !Array.isArray(inventoryData)) {
    return map;
  }

  // Helper function to process a single inventory record
  const processRecord = (record) => {
    if (!record) return;

    // Get billing account from various possible field names
    const billingAccount =
      record.billing_account ||
      record.billing_account_number ||
      null;

    // Get provider
    const provider = record.provider || null;

    // Only add to map if both billing account and provider exist
    if (billingAccount && provider) {
      const accountStr = String(billingAccount).trim();
      const providerStr = String(provider).trim();

      if (accountStr && providerStr) {
        // Normalize the account (remove spaces and dashes) for consistent lookup
        const normalizedAccount = normalizeBillingAccount(accountStr);

        // Store with normalized key for reliable matching
        // Also store with original format key to handle exact matches
        if (normalizedAccount) {
          // Use normalized key as primary (handles spacing/dash variations)
          // Always update to ensure we have the latest provider
          map.set(normalizedAccount, providerStr);
          
          // Also store with original format if different (for exact matches)
          if (accountStr !== normalizedAccount) {
            map.set(accountStr, providerStr);
          }
          
          // Also store common variations with spaces normalized to single space
          if (accountStr.includes("  ")) {
            const singleSpaceVersion = accountStr.replace(/\s+/g, " ").trim();
            if (singleSpaceVersion !== accountStr && singleSpaceVersion !== normalizedAccount) {
              map.set(singleSpaceVersion, providerStr);
            }
          }
        }
      }
    }
  };

  // Process each item in inventory data
  inventoryData.forEach((item) => {
    // Check if this is a grouped structure (has 'items' array)
    if (item.items && Array.isArray(item.items)) {
      // Grouped structure: process each item in the group
      item.items.forEach(processRecord);
    } else {
      // Non-grouped structure: process the item directly
      processRecord(item);
    }
  });

  return map;
};
